import './WebinarsViewsBreakdown.scss';
import '../../CommonStyles.scss';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@trackman/web-shared-components';
import { useGetWebinarsSummary } from 'queryHooks/Admin/analytics';
import { useState } from 'react';
import SourceBadge from '../SourceBadge/SourceBadge';
import { sortByDate, sortByString } from 'utilities/dataSorters';
import { parseDurationToSeconds } from 'utilities/videoDuration';
import { getLatestDayData } from 'utilities/getLatestDayData';
import { WebinarsSummaryResponseEntry } from 'types/admin/analytics';

type OrderBy = 'title' | 'type' | 'date' | 'duration' | 'views';
type OrderDirection = 'ASCENDING' | 'DESCENDING';

export const WebinarsViewsBreakdown = () => {
  const DEFAULT_ORDER_BY: OrderBy = 'views';
  const DEFAULT_ORDER_DIRECTION: OrderDirection = 'DESCENDING';
  const [orderBy, setOrderBy] = useState<OrderBy>(DEFAULT_ORDER_BY);
  const [orderDirection, setOrderDirection] = useState<OrderDirection>(DEFAULT_ORDER_DIRECTION);

  const handleResetSorting = () => {
    setOrderBy(DEFAULT_ORDER_BY);
    setOrderDirection(DEFAULT_ORDER_DIRECTION);
  };

  return (
    <Paper className='analytics-card webinars-views-breakdown'>
      <div className='webinars-views-breakdown__header'>
        <div className='analytics-card__title-group'>
          <span className='card-title'>Webinar details</span>
          <SourceBadge source='YOUTUBE' />
          <SourceBadge source='VIMEO' />
        </div>
        {orderBy !== DEFAULT_ORDER_BY || orderDirection !== DEFAULT_ORDER_DIRECTION ? (
          <span className='reset-sorting' onClick={handleResetSorting}>
            Reset
          </span>
        ) : null}
      </div>

      <YoutubeViewsBreakdownTable
        orderBy={orderBy}
        orderDirection={orderDirection}
        setOrderBy={setOrderBy}
        setOrderDirection={setOrderDirection}
      />
    </Paper>
  );
};

const YoutubeViewsBreakdownTable = ({
  orderBy,
  orderDirection,
  setOrderBy,
  setOrderDirection,
}: {
  orderBy: OrderBy;
  orderDirection: OrderDirection;
  setOrderBy: (orderBy: OrderBy) => void;
  setOrderDirection: (orderDirection: OrderDirection) => void;
}) => {
  const { data: webinarsSummary } = useGetWebinarsSummary();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const tableHeadCells: { id: OrderBy; label: string }[] = [
    { id: 'title', label: 'Title' },
    { id: 'type', label: 'Type' },
    { id: 'date', label: 'Date' },
    { id: 'duration', label: 'Duration' },
    { id: 'views', label: 'Views' },
  ];

  const tableData = (): WebinarsSummaryResponseEntry[] => {
    const latestDayData = getLatestDayData(webinarsSummary || [], 'date');

    if (orderBy === 'title') {
      return sortByString(latestDayData, 'title', orderDirection);
    } else if (orderBy === 'type') {
      return sortByString(latestDayData, 'kind', orderDirection);
    } else if (orderBy === 'date') {
      return sortByDate(latestDayData, (event) => new Date(event.dateUploaded), orderDirection);
    } else if (orderBy === 'duration') {
      return latestDayData.sort((a, b) => {
        const diff = parseDurationToSeconds(a.duration) - parseDurationToSeconds(b.duration);
        return orderDirection === 'ASCENDING' ? diff : -diff;
      });
    } else {
      return latestDayData.sort((a, b) => (orderDirection === 'ASCENDING' ? a.views - b.views : b.views - a.views));
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSorting = (column: OrderBy) => {
    if (orderBy !== column) {
      setOrderBy(column);
    }
    setOrderDirection(orderDirection === 'ASCENDING' ? 'DESCENDING' : 'ASCENDING');
  };

  const rows = tableData();

  return (
    <Table>
      <TableHead>
        <TableRow>
          {tableHeadCells.map((cell) => (
            <TableCell key={cell.id}>
              <TableSortLabel direction={orderDirection === 'ASCENDING' ? 'asc' : 'desc'} onClick={() => handleSorting(cell.id)}>
                {cell.label}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
          <TableRow key={index}>
            <TableCell>{row.title}</TableCell>
            <TableCell>{row.kind}</TableCell>
            <TableCell>{row.dateUploaded}</TableCell>
            <TableCell>{row.duration}</TableCell>
            <TableCell>{row.views.toLocaleString()}</TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, { label: 'All', value: -1 }]}
            labelRowsPerPage='Rows per page'
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableRow>
      </TableFooter>
    </Table>
  );
};
